import React, { useEffect } from "react";
import { WithStyles } from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import { styles } from "./EmptyOrderView.styles";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import * as UserDetailsAction from "../../../stores/user/UserDetailsAction";
import * as OrderAction from "../../../stores/orders/OrderAction";
import { useSnackbar } from "notistack";
import ToastStatusEnum from "../../../constants/ToastStatusEnum";
import { Breadcrumbs } from "../../components/Breadcrumbs/Breadcrumbs";
import UserDetailsModel from "../../../models/user/UserDetailsModel";
import { orderStats, orderTypes } from "../../../constants/ORDER_DETAILS";
import { CenteredContainer } from "../../components/CenteredContainer/CenteredContainer";
import { EmptyOrderComponent } from "./EmptyOrderComponent";
import { useAuthContext } from "../../../auth/AuthProvider";
import * as BasketAction from "../../../stores/basket/BasketAction";
import { useHistory } from "react-router-dom";

export interface IOrderOnBehalfViewProps extends WithStyles<typeof styles> {
}

const OrderOnBehalfViewPure: React.FunctionComponent<IOrderOnBehalfViewProps> = ({classes}: IOrderOnBehalfViewProps) => {

    const {t} = useTranslation();
    const dispatch = useDispatch();
    const {enqueueSnackbar} = useSnackbar();
    const history = useHistory();

    const authContext = useAuthContext();
    const adminUserId = authContext?.user?.id;
    
    const orderOnBehalfId = useSelector(state => state.orders?.newOrderId);
    const [redirect, setRedirect] = React.useState(false);

    useEffect(() => {
        dispatch(UserDetailsAction.requestAllUsersDetails());
        dispatch(BasketAction.requestBasket(adminUserId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const allUsersFromState = useSelector(state => state.userDetails?.all);

    let allUsers;
    if (allUsersFromState) {
        allUsers = Object.values(allUsersFromState)?.map((user: UserDetailsModel) => ({
            userId: user.id,
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email
        }));
    };

    const basket = useSelector((state) => state.basket);

    const orderDetails = {
        userId: "",
        orderState: orderStats[0],
        type: orderTypes[0],
        adminFee: 0.00,
        discount: 0.00,
        vat: 0.00,
        totalPrice: 0.00,
        remark: ""
    };

    useEffect(() => {
        if (orderOnBehalfId && redirect) {
            setRedirect(false);
            dispatch(OrderAction.deleteOrderIdFromState()).then((res) => {
                history.push("/admin/manage-orders/" + orderOnBehalfId);
            });
        };
    }, [orderOnBehalfId, redirect, dispatch, history]);

    const createOrder = (order) => {
        let onBehalfItems = [];
        for (let item of basket.items) {
            const onBehalfItem = {
                type: item.type,
                from: item.from,
                to: item.to,
                product: item.product,
                price: item.price,
                isins: item.isins,
                ...(item.filterableProductParameters && {
                    filterableProductParameters: item.filterableProductParameters
                })
            };
            onBehalfItems.push(onBehalfItem);
        };
        const sendDetails = {
            orderState: order.orderState,
            orderType: order.orderType,
            adminFee: Number(order.adminFee),
            discount: Number(order.discount),
            vat: Number(order.vat),
            totalPrice: Number(order.totalPrice),
            remark: order.remark,
            orderItems: onBehalfItems,
            paymentMethod: order.paymentMethod,
            quotaId: order.quotaId
        };

        dispatch(OrderAction.createEmptyOrder(order.userId, sendDetails))
            .then(() => {
                //now empty the basket for admin
                dispatch(BasketAction.deleteAll(adminUserId)).then((res) => {
                    dispatch(BasketAction.requestBasket(adminUserId));
                });
                enqueueSnackbar(t("emptyOrder.successMessage"), {variant: ToastStatusEnum.Info});
                setRedirect(true);
            })
            .catch((e) => {
                enqueueSnackbar("Error while placing order: " + e.message, {variant: ToastStatusEnum.Error});
            });
    };

    if (!basket.items || basket.items?.length === 0) {
        history.push("/admin/manage-orders/create");
    };

    return allUsers && basket
        ? <CenteredContainer>

        <Breadcrumbs
            items={
                [
                    { title: t("admin.adminPanel"), href: "/admin" },
                    { title: t("admin.manageOrdersView.manageOrders"), href: "/admin/manage-orders" },
                    { title: "Order on Behalf"}
                ]
                }
            />

        <EmptyOrderComponent order={orderDetails} createOrder={createOrder} allUsers={allUsers} onBehalf={true} items={basket.items}/>

    </CenteredContainer>
    : null;

};

export const OrderOnBehalfView = withStyles(styles)(OrderOnBehalfViewPure);
